/* ---------------------------------------------------------------------------
   Nepali, where it can be relied on.

   READ THIS BEFORE ADDING A STRING.

   **The Nepali below is provisional and has not been reviewed by a Nepali
   speaker.** It is here so the mechanism is real and the common surface is
   usable in the language of the office this is built for, not because the
   wording is settled. Anyone at DoR reading this should treat it the way this
   project treats every other unverified input: usable, marked, and to be
   replaced by the authoritative version rather than corrected by guesswork. See
   `GR-21` in ROADMAP.md, which is the item for that review.

   **What is translated is deliberately bounded.** The navigation, the screen
   titles, the network summary's headings and the agency block: a small,
   repeated, checkable vocabulary. What is NOT translated, and why:

     · **Inventory-sheet field names.** The DoR sheet these records were
       transcribed against prints them in English, so an inventory panel in
       Nepali would no longer match the paper somebody is holding beside it.
     · **The provenance prose.** Several screens carry a paragraph explaining
       what a number is worth — the vertical datum, what a map pin is not, what
       the hazard screening does not screen. A badly translated caveat is worse
       than an English one, because the reader who needs it most is the one who
       would be misled.
     · **Error messages and validation text.** Written to be acted on, often
       naming a field or a value; a mistranslation there costs somebody an edit
       they cannot make.

   **A missing translation falls back to English and is counted.** `t()` never
   returns a key, never returns an empty string, and `coverage()` reports how
   much of the catalogue exists — which is what the language control shows, so
   nobody is told the interface is in Nepali when two thirds of it is not.

   **Numerals stay Western.** Devanagari digits (०–९) are widely read in Nepal,
   but every figure in this application is a measurement quoted against a drawing
   or a sheet that uses Western digits, and a chainage that reads differently in
   two places is a chainage somebody transcribes wrong. Dates stay Gregorian for
   the same reason and a stronger one: DoR documents are dated in Bikram Sambat,
   converting to it needs a per-year table of month lengths that this project
   does not have, and a date that is wrong by three days is worse than a date in
   the wrong calendar. Both are recorded as gaps rather than approximated.

   **No Devanagari webfont ships.** Barlow is Latin-only and this application
   deliberately self-hosts its fonts so it works on one bar of signal; adding a
   Devanagari face would be several hundred kilobytes for a locale most sessions
   will not use, and fetching one from a CDN would mean the Nepali interface is
   the one that breaks offline. Devanagari therefore renders in whatever the
   device has — Nirmala UI on Windows, Noto Sans Devanagari on Android — which is
   what the font stack in src/styles/tokens.css falls through to.
   --------------------------------------------------------------------------- */

export type Locale = 'en' | 'ne';

export const LOCALES: readonly Locale[] = ['en', 'ne'];

/** What each locale calls itself. A language list in the reader's own language. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ne: 'नेपाली',
};

/**
 * The Nepali catalogue, keyed by the English string itself.
 *
 * Keyed by the English rather than by an opaque id on purpose: the English is
 * what the code reads, so a screen stays legible without a lookup table beside
 * it, and a string that gets reworded loses its translation loudly — it falls
 * back to the new English — rather than silently keeping the old one.
 */
const NE: Record<string, string> = {
  /* --- navigation and screens --- */
  Workspace: 'कार्यक्षेत्र',
  /* The nav pane's own label for the four applications, and the home page's
     name in it. `Overview` is kept below because the guide and the summary
     still use the word for the rollup panels; the destination is called Home. */
  Applications: 'अनुप्रयोगहरू',
  Home: 'गृह पृष्ठ',
  Overview: 'सिंहावलोकन',
  Register: 'दर्ता किताब',
  Network: 'सञ्जाल',
  'Bridge record': 'पुलको अभिलेख',
  '3D model': '3D मोडेल',
  /* The Scheduler's: the programme played on the 3D model. */
  Simulate: 'निर्माण अनुकरण',
  'Inventory sheet': 'सूची फाराम',
  'Network summary': 'सञ्जाल सारांश',
  /* The two construction applications' screens. */
  Programme: 'कार्यतालिका',
  'Site record': 'निर्माणस्थल अभिलेख',
  'Period report': 'अवधि प्रतिवेदन',
  'Selected structure': 'छानिएको संरचना',
  'Open record': 'अभिलेख खोल्नुहोस्',
  'Log inspection': 'निरीक्षण दर्ता गर्नुहोस्',
  'Log overall inspection': 'समग्र निरीक्षण दर्ता गर्नुहोस्',

  /* --- the agency --- */
  'Government of Nepal': 'नेपाल सरकार',
  'Ministry of Physical Infrastructure & Transport': 'भौतिक पूर्वाधार तथा यातायात मन्त्रालय',
  'Department of Roads': 'सडक विभाग',
  'Bridge Branch': 'पुल शाखा',

  /* --- the summary report --- */
  'Bridge Network Summary': 'पुल सञ्जाल सारांश',
  'Structures on file': 'अभिलेखमा रहेका संरचना',
  'Total deck length': 'कुल ढलान लम्बाइ',
  Districts: 'जिल्लाहरू',
  Roads: 'सडकहरू',
  District: 'जिल्ला',
  Province: 'प्रदेश',
  'Division road office': 'डिभिजन सडक कार्यालय',
  'Road classification': 'सडक वर्गीकरण',
  'National highway': 'राष्ट्रिय राजमार्ग',
  'Feeder road': 'सहायक सडक',
  'Provincial road': 'प्रादेशिक सडक',
  'District road': 'जिल्ला सडक',
  Condition: 'अवस्था',
  'Condition distribution': 'अवस्था वितरण',
  'Inspection compliance': 'निरीक्षण अनुपालन',
  Structures: 'संरचनाहरू',
  Good: 'राम्रो',
  Fair: 'ठीकै',
  Poor: 'कमजोर',
  Critical: 'अति कमजोर',
  'Never inspected': 'कहिल्यै निरीक्षण नभएको',
  'Not recorded': 'उल्लेख नभएको',
  Outstanding: 'बाँकी',
  Routine: 'नियमित',
  Principal: 'मुख्य',
  Inspection: 'निरीक्षण',
  Inspections: 'निरीक्षणहरू',
  Bridge: 'पुल',
  Bridges: 'पुलहरू',
  Length: 'लम्बाइ',
  Width: 'चौडाइ',
  Year: 'साल',
  Total: 'जम्मा',
  River: 'नदी',
  Road: 'सडक',
  Chainage: 'चेनेज',
  Spans: 'स्पान',
  Print: 'छाप्नुहोस्',
  'Prepared by': 'तयार गर्ने',
  'Checked by': 'जाँच गर्ने',
  'Approved by': 'स्वीकृत गर्ने',
};

const CATALOGUES: Record<Locale, Record<string, string>> = {
  en: {},
  ne: NE,
};

let current: Locale = 'en';

/**
 * Set the language everything drawn after this call reads in.
 *
 * Module state rather than a parameter threaded through every component: the
 * whole application re-renders from scratch on every store update (see
 * src/main.ts), so there is no partially-translated tree to worry about, and the
 * alternative was a locale argument on several hundred call sites.
 */
export function setLocale(next: Locale): void {
  current = next;
}

export function getLocale(): Locale {
  return current;
}

/**
 * The current locale's wording, or the English if there is none.
 *
 * Falls back to the key itself, which IS the English — so a string nobody has
 * translated appears in English rather than as a bare identifier or a blank. A
 * half-translated screen is honest; a screen full of `nav.overview` is not.
 */
export function t(english: string): string {
  return CATALOGUES[current][english] ?? english;
}

/**
 * How much of what the interface says exists in a locale.
 *
 * Reported on the language control itself. Somebody switching to Nepali is told
 * the interface is partly translated before they find out by reading it, which
 * is the same courtesy every other unverified thing in this application gets.
 */
export function coverage(locale: Locale): { translated: number; total: number } {
  const total = Object.keys(NE).length;
  return { translated: Object.keys(CATALOGUES[locale]).length, total };
}

/** Every English string the catalogue knows, for the checks. */
export function catalogueKeys(): string[] {
  return Object.keys(NE);
}
